import { MediaType } from "../../api/mediaGroupAPI";

/** 그룹 필터 값. null = 전체, UNGROUPED_FILTER = 그룹없음, number = 해당 mediaGroupId */
export const UNGROUPED_FILTER = "__ungrouped__" as const;
export type GroupFilter = number | typeof UNGROUPED_FILTER | null;

/**
 * 미디어 객체에서 소속 그룹 id 를 읽는다.
 *
 * BE 가 slides[] / photo/list / gross-photo/list 응답에 mediaGroupId 를 실어준다.
 * 신규 그룹 소속을 읽는 곳은 여기 한 곳뿐이므로, 필드가 바뀌면 이 함수만 고치면 된다.
 * (구 slide.groupId 는 문자열 그룹명이라 신규 그룹 id 와 의미가 달라 읽지 않는다.
 *  마이그레이션 전까지 구 그룹만 있는 슬라이드는 '그룹없음' 으로 보인다.)
 */
export const groupIdOf = (media: any): number | null => {
  const raw = media?.mediaGroupId;
  if (raw === null || raw === undefined || raw === "") return null;
  const id = Number(raw);
  return Number.isNaN(id) ? null : id;
};

/** 미디어 종류별 식별자 (media-groups API 의 mediaIds 에 넣는 값) */
export const mediaIdOf = (media: any, type: MediaType): number | undefined => {
  if (type === "SLIDE") return media?.slideId;
  if (type === "PHOTO") return media?.photoId;
  return media?.grossPhotoId;
};

/** mediaId → mediaGroupId 맵. moveMediaToGroup 에 넘길 현재 소속 정보. */
export const buildMembership = (
  mediaList: any[],
  type: MediaType
): Map<number, number | null> => {
  const map = new Map<number, number | null>();
  mediaList.forEach((media) => {
    const id = mediaIdOf(media, type);
    if (id != null) map.set(id, groupIdOf(media));
  });
  return map;
};

export interface GroupCounts {
  total: number;
  ungrouped: number;
  byGroup: Record<number, number>;
}

/**
 * 그룹별 미디어 개수. 목록 API 가 개수를 내려주지 않으므로 화면의 미디어 목록에서 센다.
 * (case 필터가 걸린 목록을 넘기면 그 범위 기준으로 집계된다)
 */
export const countByGroup = (mediaList: any[]): GroupCounts => {
  const byGroup: Record<number, number> = {};
  let ungrouped = 0;
  mediaList.forEach((media) => {
    const gid = groupIdOf(media);
    if (gid == null) ungrouped += 1;
    else byGroup[gid] = (byGroup[gid] ?? 0) + 1;
  });
  return { total: mediaList.length, ungrouped, byGroup };
};

/** 미디어가 현재 필터에 걸리는지 */
export const matchesGroupFilter = (media: any, filter: GroupFilter): boolean => {
  if (filter == null) return true;
  const gid = groupIdOf(media);
  if (filter === UNGROUPED_FILTER) return gid == null;
  return gid === filter;
};

/** 필터를 적용한 원본 인덱스 목록 (prev/next 이동 기준) */
export const visibleIndices = (mediaList: any[], filter: GroupFilter): number[] =>
  mediaList.reduce<number[]>((acc, media, i) => {
    if (matchesGroupFilter(media, filter)) acc.push(i);
    return acc;
  }, []);
